<script setup lang="ts">
    import { ref, onMounted } from 'vue'; // Import necessary Vue functions
    import type { Schemas } from '#shopware';

    const { allowRedirect = true } = defineProps<{
        allowRedirect?: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'change', selected: Schemas['Product']): void;
    }>();
    const { country, urlSegment } = useLanguage();
    const { t } = useI18n();

    const { getStoryblokProductLinks } = useStoryblokProductsCache();

    const router = useRouter();
    const { handleChange, getOptionGroups, getSelectedOptions, findVariantForSelectedOptions } =
        useProductConfigurator();

    const availability = ref<Record<string, boolean>>({}); // Reactive object to store availability

    const getAvailability = async () => {
        const avail = {}; // Temporary object for availability

        for (const option of getOptionGroups.value) {
            for (const value of option.options) {
                const product = await findVariantForSelectedOptions({ Größe: value.id });
                avail[value.id] = product.stock > 0; // Store true/false based on stock
            }
        }

        availability.value = avail; // Update the reactive availability object
    };

    onMounted(() => {
        getAvailability(); // Fetch availability when the component mounts
    });

    const isOptionSelected = (optionId: string) => Object.values(getSelectedOptions.value).includes(optionId);

    const storyblokProductStoriesKey = computed(() => `storyblokProductStories-${urlSegment.value}`);
    const { data: productLinks, error } = await useAsyncData(storyblokProductStoriesKey, getStoryblokProductLinks);

    if (error.value) {
        throw createError({ status: 500, message: 'Error fetching product Stories', fatal: true });
    }

    const seoLink = (variant: Schemas['Product']) =>
        productLinks.value?.length > 0 ? productLinks.value.find((e) => e.id === variant.id) : null;

    const onHandleChange = async () => {
        const variantFound = await findVariantForSelectedOptions(getSelectedOptions.value);
        const selectedOptionsVariantPath = getProductRoute(variantFound, country.value);
        if (allowRedirect && selectedOptionsVariantPath) {
            try {
                router.push(useStoryblokUrl(seoLink(variantFound)?.link) || selectedOptionsVariantPath);
            } catch (_error) {
                console.error('incorrect URL', selectedOptionsVariantPath);
            }
        } else {
            if (variantFound) emit('change', variantFound);
        }
    };
</script>

<template>
    <div
        v-if="availability"
        class="variants"
    >
        <div
            v-for="optionGroup in getOptionGroups"
            :key="optionGroup.id"
        >
            <h2>{{ optionGroup.name }}</h2>

            <fieldset>
                <legend class="sr-only">{{ t('product.choose') }} {{ optionGroup.name }}</legend>
                <div class="variant-wrapper">
                    <button
                        v-for="option in optionGroup.options"
                        :key="option.id"
                        data-testid="product-variant"
                        class="variant"
                        :class="{
                            '--selected': isOptionSelected(option.id),
                            '--unavailable': availability[option.id] === false
                        }"
                        @click="
                            handleChange(optionGroup.name ?? optionGroup.translated.name, option.id, onHandleChange)
                        "
                    >
                        <p
                            :id="`${option.id}-choice-label`"
                            data-testid="product-variant-text"
                        >
                            {{ option.name ?? option.translated.name }}
                        </p>
                    </button>
                </div>
            </fieldset>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/detail-page/front-end-detail-page-variants.scss';
</style>
